import React from 'react';
import { 
  CheckCircle2, 
  AlertTriangle, 
  ExternalLink, 
  RefreshCw, 
  Smartphone,
  ShieldCheck,
  ArrowRight
} from 'lucide-react';
import { MFA_STATUS } from '../services/mfaAnalyzer';

export default function StatusCard({ 
  status, 
  translations: t, 
  isLoading, 
  onRefresh,
  hasFido = false,
  hasAuthenticator = false
}) {
  const isOptimal = status === MFA_STATUS.AUTHENTICATOR;
  const isNeutral = status === MFA_STATUS.OTHER_MFA;
  const isWarning = status === MFA_STATUS.NO_MFA;

  const optimalTitle = t.statusOptimalTitle;
  const optimalDesc = (!hasAuthenticator && hasFido)
    ? t.statusOptimalFidoDesc
    : t.statusOptimalDesc;

  return (
    <div className={`relative overflow-hidden rounded-3xl border-2 transition-all duration-300 shadow-xl ${
      isOptimal 
        ? 'bg-gradient-to-b from-[#f0f9f4] via-white to-white border-[#008446]/40 shadow-emerald-600/10' 
        : isNeutral
        ? 'bg-gradient-to-b from-amber-50/70 via-white to-white border-amber-300 shadow-amber-600/10'
        : 'bg-gradient-to-b from-rose-50/90 via-white to-white border-[#e30613]/40 shadow-rose-600/15'
    }`}>
      
      {/* Top brand accent bar */}
      <div className={`h-2.5 w-full ${
        isOptimal ? 'bg-[#008446]' : isNeutral ? 'bg-amber-500' : 'bg-[#e30613]'
      }`} />

      <div className="p-6 sm:p-10 text-center flex flex-col items-center">
        
        {/* Refresh button top right */}
        <div className="w-full flex justify-end mb-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-slate-500 hover:text-slate-800 bg-white border border-slate-200 hover:border-slate-300 rounded-full shadow-2xs transition-colors disabled:opacity-50"
            title={t.refreshStatus}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-[#008446]' : ''}`} />
            <span>{t.refreshStatus}</span>
          </button>
        </div>

        {/* Big Central Status Icon with Subtle Ambient Animations */}
        <div className="mb-6 relative">
          
          {/* Subtle Ambient Glow Aura behind icon */}
          {isOptimal && (
            <div className="absolute inset-0 rounded-3xl bg-[#008446] blur-2xl opacity-30 animate-aura-green pointer-events-none" />
          )}
          {isNeutral && (
            <div className="absolute inset-0 rounded-3xl bg-amber-400 blur-2xl opacity-20 pointer-events-none" />
          )}
          {isWarning && (
            <div className="absolute inset-0 rounded-3xl bg-[#e30613] blur-2xl opacity-25 animate-beacon-halo pointer-events-none" />
          )}

          {/* Main Icon Box */}
          <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-3xl flex items-center justify-center text-white shadow-2xl transition-transform duration-300 hover:scale-105 relative z-10 ${
            isOptimal 
              ? 'bg-gradient-to-tr from-[#008446] to-[#56b382] shadow-[#008446]/30' 
              : isNeutral
              ? 'bg-gradient-to-tr from-amber-600 to-amber-400 shadow-amber-600/30 animate-phone-vibrate'
              : 'bg-gradient-to-tr from-[#e30613] to-orange-500 shadow-rose-600/30'
          }`}>
            {isOptimal && <ShieldCheck className="w-14 h-14 sm:w-16 sm:h-16" />}
            {isNeutral && <Smartphone className="w-14 h-14 sm:w-16 sm:h-16" />}
            {isWarning && <AlertTriangle className="w-14 h-14 sm:w-16 sm:h-16" />}
          </div>

          {/* Floating Mini Badge with Scenario Micro-Animations */}
          <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-2 border-white flex items-center justify-center shadow-md z-20 ${
            isOptimal 
              ? 'bg-[#008446] text-white animate-float-badge' 
              : isNeutral 
              ? 'bg-amber-500 text-white' 
              : 'bg-[#e30613] text-white animate-exclamation-pulse'
          }`}>
            {isOptimal && <CheckCircle2 className="w-5 h-5" />}
            {isNeutral && <ArrowRight className="w-4 h-4 animate-arrow-nudge" />}
            {isWarning && <span className="font-bold text-sm">!</span>}
          </div>

        </div>

        {/* Status Badge */}
        <div className="mb-3">
          <span className={`inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-transform duration-200 hover:scale-105 ${
            isOptimal 
              ? 'bg-[#dbf1e4] text-[#005930] border border-[#8cceaa]' 
              : isNeutral
              ? 'bg-amber-100 text-amber-900 border border-amber-300'
              : 'bg-rose-100 text-[#9e030c] border border-rose-300'
          }`}>
            {isOptimal && t.statusOptimalBadge}
            {isNeutral && t.statusNeutralBadge}
            {isWarning && t.statusWarningBadge}
          </span>
        </div>

        {/* Big Bold Headline */}
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2 max-w-xl">
          {isOptimal && optimalTitle}
          {isNeutral && t.statusNeutralTitle}
          {isWarning && t.statusWarningTitle}
        </h2>

        {/* Short friendly explanation */}
        <p className="text-sm sm:text-base text-slate-600 max-w-lg mb-8 leading-relaxed">
          {isOptimal && optimalDesc}
          {isNeutral && t.statusNeutralDesc}
          {isWarning && t.statusWarningDesc}
        </p>

        {/* Primary Action Button or Success Pill */}
        <div className="w-full max-w-md flex flex-col items-center gap-3">
          {isOptimal ? (
            <div className="w-full py-3.5 px-6 rounded-2xl bg-[#f0f9f4] border border-[#8cceaa] text-[#005930] font-semibold text-sm flex items-center justify-center gap-2 shadow-xs relative overflow-hidden group">
              {/* Subtle Gleam / Shimmer effect */}
              <div className="absolute inset-0 shimmer-effect pointer-events-none opacity-40" />
              <CheckCircle2 className="w-5 h-5 text-[#008446] shrink-0" />
              <span className="relative z-10">{t.statusOptimalSubtitle}</span>
            </div>
          ) : (
            <a
              href="https://aka.ms/mfasetup"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full py-4 px-8 rounded-2xl font-bold text-base text-white shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 ${
                isNeutral
                  ? 'bg-[#008446] hover:bg-[#00703b] shadow-[#008446]/25 hover:shadow-[#008446]/35'
                  : 'bg-[#e30613] hover:bg-[#c0040f] shadow-[#e30613]/30 ring-4 ring-rose-200/70 hover:ring-rose-300/80 hover:scale-[1.01]'
              }`}
            >
              <span>{isNeutral ? t.statusNeutralAction : t.statusWarningAction}</span>
              <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
            </a>
          )}
        </div>

      </div>
    </div>
  );
}
